import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import CreativesList from './Content/CreativesList';
import SettingsPage from './Settings/SettingsPage';
import AccessPage from './AccessPage';
import CreateCreativeModal from './CreateCreativeModal';
import NotificationPanel from './NotificationPanel';
import MarkAppFooter from './MarkAppFooter';
import VKAccountSwitcher from './VKAccountSwitcher';
import SubscriptionBanner from './SubscriptionBanner';
import LimitReachedModal from './LimitReachedModal';
import { FileIcon, BriefcaseIcon, SettingsIcon, ChartIcon, BuildingIcon, ListIcon, UsersIcon, FolderIcon, FileTextIcon, ReceiptIcon, BarChartIcon, DashboardIcon, ContractIcon, LayersIcon } from './Icons';
import './MarkAppDashboard.css';
import api from '../services/api';
import { useNetworkStatus } from '../context/NetworkStatusContext';

// Lazy loading тяжелых компонентов для ускорения начальной загрузки
const AnalyticsDashboard = lazy(() => import('./AnalyticsDashboard'));
const ContractsPage = lazy(() => import('./ContractsPage'));
const ActsPage = lazy(() => import('./ActsPage'));
const StatisticsPage = lazy(() => import('./StatisticsPage'));
const PlatformsPage = lazy(() => import('./PlatformsPage'));
const BillingDashboard = lazy(() => import('./BillingDashboard'));
const PaymentsPage = lazy(() => import('./PaymentsPage'));
const CounterpartiesPage = lazy(() => import("./CounterpartiesPage"));

const MarkAppDashboard = () => {
  const navigate = useNavigate();
  const [mainSection, setMainSection] = useState('content');
  const [subSection, setSubSection] = useState('publications');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshCreatives, setRefreshCreatives] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [hasActiveVkAccount, setHasActiveVkAccount] = useState(null);
  const [vkAccountLoading, setVkAccountLoading] = useState(true);
  const { isOffline } = useNetworkStatus();
  
  // Реф для меню пользователя
  const userMenuRef = useRef(null);

  // Навигация через hash (например, #billing-payments)
  useEffect(() => {
    const applySectionFromHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'billing-payments') {
        setShowSettings(false);
        setMainSection('billing');
        setSubSection('payments');
      } else if (hash === 'billing-tariffs') {
        setShowSettings(false);
        setMainSection('billing');
        setSubSection('tariffs');
      }
    };

    applySectionFromHash();
    window.addEventListener('hashchange', applySectionFromHash);
    return () => window.removeEventListener('hashchange', applySectionFromHash);
  }, []);

  // Загрузка данных пользователя
  useEffect(() => {
    const loadUserData = () => {
      try {
        const storedUserData = localStorage.getItem('user_data');
        if (storedUserData) {
          const user = JSON.parse(storedUserData);
          setUserData(user);
        }
      } catch (error) {
        // Ошибка загрузки данных пользователя - игнорируем
      }
    };
    
    loadUserData();
  }, []);

  // Проверяем наличие активных VK кабинетов
  const fetchVkAccounts = async () => {
    try {
      setVkAccountLoading(true);
      const { data } = await api.get('/vk-accounts/');
      const hasActive = Array.isArray(data) && data.some(account => account.is_active);
      setHasActiveVkAccount(hasActive);
    } catch (error) {
      setHasActiveVkAccount(false);
    } finally {
      setVkAccountLoading(false);
    }
  };

  useEffect(() => {
    fetchVkAccounts();
  }, []);
  
  // Перезагружаем VK кабинеты при закрытии настроек
  useEffect(() => {
    if (!showSettings) {
      fetchVkAccounts();
    }
  }, [showSettings]);

  // Проверка активной подписки при загрузке
  useEffect(() => {
    const checkSubscription = async () => {
      if (!userData?.id) {
        setSubscriptionLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`/api/v1/billing/subscription?user_id=${userData.id}`);
        if (response.ok) {
          await response.json();
          setHasActiveSubscription(true);
        } else {
          setHasActiveSubscription(false);
        }
      } catch (error) {
        // Ошибка проверки подписки - игнорируем
        setHasActiveSubscription(false);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    checkSubscription();
  }, [userData]);

  // Редирект на billing при отсутствии подписки
  useEffect(() => {
    if (subscriptionLoading || hasActiveSubscription === null) return;
    
    if (hasActiveSubscription === false && !showSettings && mainSection !== 'billing') {
      setMainSection('billing');
      setSubSection('tariffs');
    }
  }, [hasActiveSubscription, subscriptionLoading, mainSection, showSettings]);

  // Закрытие меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);
  
  // Получаем отображаемое имя пользователя
  const displayName = userData?.full_name || userData?.username || 'Пользователь';

  const useFullWidthLayout = (
    (mainSection === 'management' && (subSection === 'projects' || subSection === 'dashboard' || subSection === 'counterparties' || subSection === 'platforms')) ||
    (mainSection === 'reporting' && (subSection === 'reports' || subSection === 'acts')) ||
    (mainSection === 'content' && subSection === 'publications')
  );

  return (
    <div className="dashboard-container">
      <div className="dashboard">
        {/* Верхняя навигация */}
        <nav className="top-nav">
          <div className="top-nav-content">
            <a href="https://markapp.pro" className="nav-logo" target="_blank" rel="noopener noreferrer">
              <img src="/markapp-logo.png" alt="MarkApp" className="logo-image" />
            </a>
            
            <div className="main-nav-buttons">
              <button
                className={mainSection === 'content' ? 'active' : ''}
                onClick={() => { setMainSection('content'); setSubSection('publications'); setShowSettings(false); }}
              >
                <span className="nav-icon"><FileIcon /></span>
                Контент
              </button>
              <button
                className={mainSection === 'management' ? 'active' : ''}
                onClick={() => { setMainSection('management'); setSubSection('projects'); setShowSettings(false); }}
              >
                <span className="nav-icon"><BriefcaseIcon /></span>
                Управление
              </button>
              <button
                className={mainSection === 'reporting' ? 'active' : ''}
                onClick={() => { setMainSection('reporting'); setSubSection('reports'); setShowSettings(false); }}
              >
                <span className="nav-icon"><ChartIcon /></span>
                Отчётность
              </button>
            </div>

            <div className="nav-right">
              <NotificationPanel />
              <div className="user-menu-container" ref={userMenuRef}>
                <button 
                  className="user-menu-btn" 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <span className="user-icon">👤</span>
                  <span className="user-name">{displayName}</span>
                  <span className="dropdown-arrow">▼</span>
                </button>
                {showUserMenu && (
                  <div className="user-dropdown">
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        setShowSettings(true);
                        setShowUserMenu(false);
                      }}
                    >
                      <span className="icon-inline"><SettingsIcon /></span> Настройки
                    </button>
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        setMainSection('access');
                        setShowSettings(false);
                        setShowUserMenu(false);
                      }}
                    >
                      <span className="icon-inline">📱</span> Доступ
                    </button>
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        setMainSection('billing');
                        setSubSection('tariffs');
                        setShowSettings(false);
                        setShowUserMenu(false);
                      }}
                    >
                      <span className="icon-inline"><ReceiptIcon /></span> Платежи
                    </button>
                    <hr className="dropdown-divider" />
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        setShowAccountSwitcher(true);
                        setShowUserMenu(false);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.74 10.03 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z" fill="currentColor"/>
                      </svg>
                      Сменить аккаунт
                    </button>
                    <button 
                      className="dropdown-item danger"
                      onClick={() => {
                        // Полный выход
                        localStorage.removeItem('access_token');
                        localStorage.removeItem('user_data');
                        localStorage.removeItem('refresh_token');
                        sessionStorage.clear();
                        navigate('/login', { replace: true });
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 8L15.59 9.41L17.17 11H9V13H17.17L15.59 14.59L17 16L21 12L17 8ZM5 5H12V3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H12V19H5V5Z" fill="currentColor"/>
                      </svg>
                      Выйти
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Подменю для Управление */}
        {mainSection === 'management' && !showSettings && (
          <div className="submenu">
            <div className="submenu-content">
              <button
                className={`dashboard-btn ${subSection === 'dashboard' ? 'active' : ''}`}
                onClick={() => setSubSection('dashboard')}
                style={{ display: 'none' }}
              >
                <DashboardIcon /> Дашборд
              </button>
              <button
                className={subSection === 'projects' ? 'active' : ''}
                onClick={() => setSubSection('projects')}
              >
                <ContractIcon /> Договоры
              </button>
              <button
                className={subSection === 'counterparties' ? 'active' : ''}
                onClick={() => setSubSection('counterparties')}
              >
                <BuildingIcon /> Контрагенты
              </button>
              <button
                className={subSection === 'platforms' ? 'active' : ''}
                onClick={() => setSubSection('platforms')}
              >
                <LayersIcon /> Площадки
              </button>
            </div>
          </div>
        )}

        {/* Подменю для Отчётность */}
        {mainSection === 'reporting' && !showSettings && (
          <div className="submenu">
            <div className="submenu-content">
              <button
                className={subSection === 'reports' ? 'active' : ''}
                onClick={() => setSubSection('reports')}
              >
                <BarChartIcon /> Статистика
              </button>
              <button
                className={subSection === 'acts' ? 'active' : ''}
                onClick={() => setSubSection('acts')}
              >
                <FileTextIcon /> Акты
              </button>
            </div>
          </div>
        )}

        {/* Подменю для Платежи */}
        {mainSection === 'billing' && !showSettings && (
          <div className="submenu">
            <div className="submenu-content">
              <button
                className={subSection === 'tariffs' ? 'active' : ''}
                onClick={() => setSubSection('tariffs')}
              >
                <ReceiptIcon /> Тарифы
              </button>
              <button
                className={subSection === 'payments' ? 'active' : ''}
                onClick={() => setSubSection('payments')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle', marginRight: '6px' }}>
                  <path d="M2 8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5H7C4 20.5 2 19 2 15.5V8.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 9.5H22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 16.5H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14.5 16.5H10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                История
              </button>
            </div>
          </div>
        )}

        {/* VK ОРД баннер (вне main-content для консистентности) */}
        {!isOffline && !showSettings && hasActiveVkAccount === false && !vkAccountLoading && (
          <div className="vk-warning-banner">
            <div className="vk-warning-icon">⚠️</div>
            <div className="vk-warning-text">
              <div className="vk-warning-title">У вас не добавлен ни один кабинет в VK ОРД</div>
              <p>Добавьте кабинет в настройках, чтобы синхронизировать договоры, креативы и статистику.</p>
            </div>
            <button
              className="vk-warning-action"
              onClick={() => {
                setShowSettings(true);
                setMainSection('content');
                setSubSection('publications');
              }}
            >
              Открыть настройки
            </button>
          </div>
        )}

        {/* Основной контент */}
        <div className={`main-content ${useFullWidthLayout ? 'full-width-content' : ''}`}>
          {isOffline ? (
            <div className="offline-placeholder">
              <div className="offline-placeholder-icon">📡</div>
              <h2>Нет связи с сервером</h2>
              <p>Проверьте подключение к интернету или повторите попытку позже.</p>
            </div>
          ) : (
            <>
              {/* Настройки */}
              {showSettings && (
                <>
                  <SettingsPage />
                </>
              )}

              {/* Доступ к iOS приложению */}
              {!showSettings && mainSection === 'access' && (
                <>
                  <AccessPage />
                </>
              )}

              {/* ПЛАТЕЖИ */}
              {!showSettings && mainSection === 'billing' && (
                <Suspense fallback={<div style={{padding: '20px', textAlign: 'center'}}>Загрузка...</div>}>
                  {subSection === 'tariffs' ? (
                    <BillingDashboard />
                  ) : subSection === 'payments' ? (
                    <PaymentsPage />
                  ) : (
                    <BillingDashboard />
                  )}
                </Suspense>
              )}

              {/* КОНТЕНТ - Публикации */}
              {!showSettings && mainSection === 'content' && subSection === 'publications' && (
                <div className="creatives-page">
                  {userData?.id && (
                    <SubscriptionBanner 
                      userId={userData.id} 
                      onNavigateToTariffs={() => {
                        setMainSection('billing');
                        setSubSection('tariffs');
                      }}
                    />
                  )}
                  {subscriptionLoading ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                    </div>
                  ) : hasActiveSubscription === true ? (
                    <CreativesList />
                  ) : hasActiveSubscription === false ? (
                    <div className="no-subscription-block">
                      <div className="no-subscription-icon">🔒</div>
                      <h2>У вас нет активной подписки</h2>
                      <p>Для доступа к контенту активируйте Trial на 14 дней или выберите подходящий тариф</p>
                      <button 
                        className="activate-subscription-btn"
                        onClick={() => { setMainSection('billing'); setSubSection('tariffs'); }}
                      >
                        Выбрать тариф
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

                          {/* УПРАВЛЕНИЕ - разделы */}
              {!showSettings && mainSection === 'management' && (
                subscriptionLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                  </div>
                ) : hasActiveSubscription === false ? (
                  <div className="no-subscription-block">
                    <div className="no-subscription-icon">🔒</div>
                    <h2>У вас нет активной подписки</h2>
                    <p>Для доступа к управлению активируйте Trial на 14 дней или выберите подходящий тариф</p>
                    <button 
                      className="activate-subscription-btn"
                      onClick={() => { setMainSection('billing'); setSubSection('tariffs'); }}
                    >
                      Выбрать тариф
                    </button>
                  </div>
                ) : (
                  <Suspense fallback={<div style={{padding: '20px', textAlign: 'center'}}>Загрузка...</div>}>
                    {subSection === 'dashboard' ? (
                      <>
                        {/* Дашборд временно скрыт - показываем Договоры */}
                        <ContractsPage />
                      </>
                    ) : subSection === 'counterparties' ? (
                      <>
                        <CounterpartiesPage />
                      </>
                    ) : subSection === 'projects' ? (
                      <>
                        <ContractsPage />
                      </>
                    ) : subSection === 'platforms' ? (
                      <>
                        <PlatformsPage />
                      </>
                    ) : (
                      <div className="empty-state">
                        <span className="empty-icon"><SettingsIcon /></span>
                        <h2>Раздел в разработке</h2>
                        <p>Выберите раздел из меню слева</p>
                      </div>
                    )}
                  </Suspense>
                )
              )}

              {/* ОТЧЁТНОСТЬ - разделы */}
              {!showSettings && mainSection === 'reporting' && (
                subscriptionLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                  </div>
                ) : hasActiveSubscription === false ? (
                  <div className="no-subscription-block">
                    <div className="no-subscription-icon">🔒</div>
                    <h2>У вас нет активной подписки</h2>
                    <p>Для доступа к отчётности активируйте Trial на 14 дней или выберите подходящий тариф</p>
                    <button 
                      className="activate-subscription-btn"
                      onClick={() => { setMainSection('billing'); setSubSection('tariffs'); }}
                    >
                      Выбрать тариф
                    </button>
                  </div>
                ) : (
                  <Suspense fallback={<div style={{padding: '20px', textAlign: 'center'}}>Загрузка...</div>}>
                    {subSection === 'acts' ? (
                      <>
                        <ActsPage />
                      </>
                    ) : subSection === 'reports' ? (
                      <>
                        <StatisticsPage />
                      </>
                    ) : (
                      <div className="empty-state">
                        <span className="empty-icon"><ChartIcon /></span>
                        <h2>Раздел в разработке</h2>
                        <p>Выберите раздел из меню слева</p>
                      </div>
                    )}
                  </Suspense>
                )
              )}
            </>
          )}
        </div>

        {/* Модальное окно создания креатива */}
        <CreateCreativeModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            setRefreshCreatives(prev => prev + 1);
          }}
        />

        {/* Модальное окно переключения VK аккаунтов */}
        <VKAccountSwitcher
          show={showAccountSwitcher}
          onClose={() => setShowAccountSwitcher(false)}
          onSwitch={() => {
            setShowAccountSwitcher(false);
            // Перезагрузка данных произойдет автоматически через window.location.reload() в VKAccountSwitcher
          }}
        />
        
        {/* Модальное окно превышения лимита */}
        {showLimitModal && limitError && (
          <LimitReachedModal
            limitType={limitError.limit_type || 'creatives'}
            used={limitError.current_usage || 0}
            limit={limitError.limit_value || 10}
            tariffName={limitError.tariff_name || 'Trial'}
            onUpgrade={() => {
              setShowLimitModal(false);
              setMainSection('billing');
            }}
            onClose={() => setShowLimitModal(false)}
          />
        )}
      </div>
      <MarkAppFooter />
    </div>
  );
};

export default MarkAppDashboard;
